import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const cardVariants = cva(
  "rounded-apx-xl border bg-card text-card-foreground transition-all duration-apx-normal ease-apx-ease",
  {
    variants: {
      variant: {
        default: "border-apx-border shadow-apx-sm",
        elevated: "border-apx-border shadow-apx-md hover:shadow-apx-lg",
        interactive:
          "border-apx-border shadow-apx-sm hover:shadow-apx-md hover:-translate-y-1 hover:border-apx-border-strong cursor-pointer",
        accent:
          "border-apx-border shadow-apx-sm relative overflow-hidden before:absolute before:top-0 before:left-0 before:right-0 before:h-[3px] before:bg-gradient-to-r before:from-insight-500 before:to-insight-600",
        "accent-animated":
          "border-apx-border shadow-apx-sm relative overflow-hidden before:absolute before:top-0 before:left-0 before:right-0 before:h-[3px] before:bg-gradient-to-r before:from-insight-500 before:to-insight-600 before:transform before:scale-x-0 before:origin-left before:transition-transform before:duration-apx-slow hover:before:scale-x-100 hover:-translate-y-1 hover:shadow-apx-glow-insight",
        ghost: "border-transparent shadow-none bg-transparent",
        outline: "border-apx-border-strong bg-transparent shadow-none",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant, className }))}
      {...props}
    />
  )
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "text-xl font-semibold leading-none tracking-tight text-apx-text-primary",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-apx-text-secondary", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

// CardAccent - A card with a gradient top border that animates on hover
const CardAccent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    accentColor?: "insight" | "vault" | "allocator" | "custom"
    customGradient?: string
  }
>(({ className, accentColor = "insight", customGradient, children, ...props }, ref) => {
  const gradientMap = {
    insight: "from-insight-500 to-insight-600",
    vault: "from-vault-primary to-vault-dark",
    allocator: "from-allocator-primary to-allocator-dark",
    custom: customGradient || "from-insight-500 to-insight-600",
  }

  const glowMap = {
    insight: "hover:shadow-apx-glow-insight",
    vault: "hover:shadow-apx-glow-vault",
    allocator: "hover:shadow-apx-glow-allocator",
    custom: "hover:shadow-apx-glow",
  }

  return (
    <div
      ref={ref}
      className={cn(
        "rounded-apx-xl border border-apx-border bg-card text-card-foreground shadow-apx-sm",
        "relative overflow-hidden transition-all duration-apx-normal ease-apx-ease",
        "hover:-translate-y-1",
        glowMap[accentColor],
        className
      )}
      {...props}
    >
      {/* Animated gradient top border */}
      <div
        className={cn(
          "absolute top-0 left-0 right-0 h-[3px]",
          "bg-gradient-to-r",
          gradientMap[accentColor],
          "transform scale-x-0 origin-left transition-transform duration-apx-slow",
          "group-hover:scale-x-100"
        )}
        aria-hidden="true"
      />
      {/* Static gradient border visible on hover via parent */}
      <div
        className={cn(
          "absolute top-0 left-0 right-0 h-[3px]",
          "bg-gradient-to-r",
          gradientMap[accentColor],
          "opacity-0 transition-opacity duration-apx-slow",
          "[.group:hover_&]:opacity-100"
        )}
        aria-hidden="true"
      />
      {children}
    </div>
  )
})
CardAccent.displayName = "CardAccent"

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  CardAccent,
  cardVariants,
}
